import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { CalendarOff, RefreshCw } from "lucide-react"
import { useMemo } from "react"

import { AbsenceActiveCard } from "@/components/absence/absence-active-card"
import { AbsenceApplyCard } from "@/components/absence/absence-apply-card"
import { AbsenceHistoryCard } from "@/components/absence/absence-history-card"
import { Button } from "@/components/button"
import { PageIntroSplitHeader } from "@/components/layout/page-intro-split-header"
import { MetricTile } from "@/components/shared/metric-tile"
import { RefreshButton } from "@/components/shared/refresh-button"
import { SectionKicker } from "@/components/shared/section-kicker"
import { StatusAlert } from "@/components/shared/status-alert"
import { Card, CardContent } from "@/components/ui/card"
import { DashboardSkeleton } from "@/components/ui/page-skeletons"
import { absenceTrackingApi } from "@/lib/absence-tracking-api"
import { createAbsencesUiStore } from "@/lib/stores/absences-ui-store"
import { useAbsencesRouteStore } from "@/lib/stores/absences-ui-store"

const absenceDashboardQueryKey = ["absence-tracking", "me"] as const

export const Route = createFileRoute("/_authed/absences")({
  beforeLoad: () => ({
    absencesStore: createAbsencesUiStore(),
  }),
  component: AbsencesPage,
})

function AbsencesPage() {
  const store = useAbsencesRouteStore()
  const dashboardQuery = useQuery({
    queryKey: absenceDashboardQueryKey,
    queryFn: absenceTrackingApi.getMyDashboard,
  })

  const metrics = useMemo(() => {
    const data = dashboardQuery.data
    return {
      active:
        data?.activeItems.filter((item) => item.state === "approved").length ??
        0,
      pending:
        data?.activeItems.filter((item) => item.state === "pending").length ??
        0,
      history: data?.historyItems.length ?? 0,
    }
  }, [dashboardQuery.data])

  if (dashboardQuery.isLoading) {
    return <DashboardSkeleton cardCount={3} className="grid gap-4 md:gap-5" />
  }

  if (dashboardQuery.isError && !dashboardQuery.data) {
    return (
      <div className="grid gap-4 md:gap-5">
        <StatusAlert tone="danger">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span>
              {dashboardQuery.error instanceof Error
                ? dashboardQuery.error.message
                : "Failed to load your absence dashboard"}
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void dashboardQuery.refetch()}
            >
              <RefreshCw className="size-3.5" />
              Retry
            </Button>
          </div>
        </StatusAlert>
      </div>
    )
  }

  const handleRetry = () => void dashboardQuery.refetch()

  return (
    <div className="grid gap-4 md:gap-5">
      <Card className="page-intro border-primary/10">
        <PageIntroSplitHeader
          headerClassName="gap-4 md:gap-5"
          kicker={
            <SectionKicker icon={<CalendarOff />}>Absences</SectionKicker>
          }
          title="Let the guild know when you will be away"
          titleClassName="text-4xl leading-none font-semibold md:text-5xl"
          description="Let your guild know when you'll be away. Keep your absences visible and browse your past time off — no Discord needed."
          descriptionClassName="max-w-3xl text-base leading-7"
          actions={
            <RefreshButton
              isFetching={dashboardQuery.isFetching}
              onClick={() => void dashboardQuery.refetch()}
            />
          }
          leftPanelClassName="gap-3"
          metrics={
            <>
              <MetricTile label="Active" value={String(metrics.active)} />
              <MetricTile label="Pending" value={String(metrics.pending)} />
              <MetricTile label="History" value={String(metrics.history)} />
            </>
          }
        />
      </Card>

      <AbsenceApplyCard
        store={store}
        reviewRequired={dashboardQuery.data?.reviewRequired ?? false}
      />

      <AbsenceActiveCard
        activeItems={dashboardQuery.data?.activeItems ?? []}
        isLoading={dashboardQuery.isFetching && !dashboardQuery.isLoading}
        isError={dashboardQuery.isError}
        onRetry={handleRetry}
      />

      <AbsenceHistoryCard
        historyItems={dashboardQuery.data?.historyItems ?? []}
      />
    </div>
  )
}
